import { MemberRoleEnum, MemberStatusEnum, PermissionsEnum, resolveMemberRole } from '@novu/shared';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useId, useState } from 'react';
import {
  RiAddCircleLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiLoader4Line,
  RiMailSendLine,
  RiUserAddLine,
} from 'react-icons/ri';
import { del, get, post, put } from '@/api/api.client';
import { Avatar, AvatarFallback } from '@/components/primitives/avatar';
import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/primitives/select';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { useHasPermission } from '@/hooks/use-has-permission';
import { getJwtToken, isJwtValid } from './jwt-manager';

/** Owner is implicit and never offered; the API rejects assigning it. */
const ROLE_OPTIONS = [
  { value: MemberRoleEnum.ADMIN, label: 'Admin', hint: 'Full access, including organization settings' },
  { value: MemberRoleEnum.AUTHOR, label: 'Author', hint: 'Create and run workflows' },
  {
    value: MemberRoleEnum.INTEGRATOR,
    label: 'Integrator',
    hint: 'Workflows, logs and API keys; no access to provider credentials',
  },
  { value: MemberRoleEnum.VIEWER, label: 'Viewer', hint: 'Read-only access' },
] as const;

function getRoleLabel(role: MemberRoleEnum | undefined): string {
  if (!role) return 'Unknown';

  const resolved = resolveMemberRole(role);

  if (resolved === MemberRoleEnum.OWNER) return 'Owner';

  return ROLE_OPTIONS.find((option) => option.value === resolved)?.label ?? resolved.replace('org:', '');
}

function getRoleBadgeStyle(role: MemberRoleEnum | undefined): string {
  switch (role && resolveMemberRole(role)) {
    case MemberRoleEnum.OWNER:
      return 'bg-primary-100 text-primary-700';
    case MemberRoleEnum.ADMIN:
      return 'bg-blue-100 text-blue-700';
    case MemberRoleEnum.AUTHOR:
      return 'bg-purple-100 text-purple-700';
    case MemberRoleEnum.INTEGRATOR:
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-neutral-100 text-foreground-700';
  }
}

/**
 * Shape returned by `GET /v1/organizations/members` on the community API. Invited members are
 * plain member documents with `memberStatus: invited` and no `_userId` yet.
 */
type SelfHostedMember = {
  _id: string;
  _userId?: string;
  _organizationId: string;
  roles?: MemberRoleEnum[];
  memberStatus?: MemberStatusEnum;
  user?: { _id: string; firstName?: string; lastName?: string; email?: string };
  invite?: { email: string; token: string; invitationDate?: string; _inviterId?: string };
};

/**
 * Reads the signed-in user id straight from the JWT. Going through the auth context would import the
 * self-hosted `@clerk/react` shim, and its unguarded `window.Clerk` side effect must never run in
 * cloud builds that load this module through the settings page.
 */
function getCurrentUserId(): string | undefined {
  const jwt = getJwtToken();
  if (!isJwtValid(jwt)) return undefined;

  try {
    return JSON.parse(atob((jwt as string).split('.')[1]))._id;
  } catch {
    return undefined;
  }
}

function getInitials(value: string): string {
  return value
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getMemberName(member: SelfHostedMember): string {
  const fullName = [member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ').trim();

  return fullName || member.user?.email || member.invite?.email || 'Unknown member';
}

function getMemberEmail(member: SelfHostedMember): string {
  return member.user?.email || member.invite?.email || '';
}

function getMemberRole(member: SelfHostedMember): MemberRoleEnum | undefined {
  return member.roles?.[0];
}

function isOwner(member: SelfHostedMember): boolean {
  const role = getMemberRole(member);

  return !!role && resolveMemberRole(role) === MemberRoleEnum.OWNER;
}

/** The community API mails this URL itself; we surface the same one for manual sharing. */
function buildInviteUrl(token: string): string {
  return `${window.location.origin}/auth/invitation/${token}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch {
    return false;
  }
}

function MemberListItem({
  member,
  currentUserId,
  onRemove,
  onChangeRole,
  isRemoving,
  isUpdatingRole,
  canManageMembers,
}: {
  member: SelfHostedMember;
  currentUserId?: string;
  onRemove: (memberId: string) => void;
  onChangeRole: (memberId: string, role: MemberRoleEnum) => void;
  isRemoving: boolean;
  isUpdatingRole: boolean;
  canManageMembers: boolean;
}) {
  const isCurrentUser = !!currentUserId && member._userId === currentUserId;
  const memberIsOwner = isOwner(member);
  const role = getMemberRole(member);
  const name = getMemberName(member);
  // Owners are not reassignable, and demoting yourself is an easy way to lock yourself out.
  const canEditRole = canManageMembers && !memberIsOwner && !isCurrentUser;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.2 }}
      className="flex items-center justify-between border-b border-neutral-100 py-3 last:border-b-0"
    >
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-neutral-100 text-foreground-700 text-sm font-medium">
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground-950">
            {name}
            {isCurrentUser && <span className="ml-1.5 text-foreground-600">(You)</span>}
          </span>
          <span className="text-xs text-foreground-600">{getMemberEmail(member) || 'Email hidden'}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {canEditRole ? (
          <Select
            value={role ? resolveMemberRole(role) : undefined}
            onValueChange={(value) => onChangeRole(member._id, value as MemberRoleEnum)}
            disabled={isUpdatingRole}
          >
            <SelectTrigger className="h-8 w-28 text-xs">
              <SelectValue placeholder={getRoleLabel(role)} />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getRoleBadgeStyle(role)}`}>
            {getRoleLabel(role)}
          </span>
        )}
        {canManageMembers && !memberIsOwner && (
          <Button
            variant="secondary"
            mode="ghost"
            size="sm"
            onClick={() => onRemove(member._id)}
            disabled={isCurrentUser || isRemoving}
            title={isCurrentUser ? 'You cannot remove yourself' : 'Remove member'}
            className="h-8 w-8 p-0"
          >
            {isRemoving ? (
              <RiLoader4Line className="size-4 animate-spin" />
            ) : (
              <RiDeleteBinLine className="size-4 text-destructive" />
            )}
          </Button>
        )}
      </div>
    </motion.div>
  );
}

function InvitationListItem({
  member,
  onCopyLink,
  onResend,
  onRevoke,
  isResending,
  isRemoving,
}: {
  member: SelfHostedMember;
  onCopyLink: (token: string) => void;
  onResend: (memberId: string) => void;
  onRevoke: (memberId: string) => void;
  isResending: boolean;
  isRemoving: boolean;
}) {
  const email = member.invite?.email || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.2 }}
      className="flex items-center justify-between border-b border-neutral-100 py-3 last:border-b-0"
    >
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-neutral-100 text-foreground-700 text-sm font-medium">
            {getInitials(email || '?')}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground-950">{email || 'Invited member'}</span>
          <span className="text-xs text-foreground-600">Pending invitation</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {member.invite?.token && (
          <Button
            variant="secondary"
            mode="ghost"
            size="sm"
            onClick={() => onCopyLink(member.invite?.token as string)}
            className="h-8 gap-1.5"
          >
            <RiFileCopyLine className="size-4" />
            Copy link
          </Button>
        )}
        <Button
          variant="secondary"
          mode="ghost"
          size="sm"
          onClick={() => onResend(member._id)}
          disabled={isResending}
          className="h-8 gap-1.5"
        >
          {isResending ? <RiLoader4Line className="size-4 animate-spin" /> : <RiMailSendLine className="size-4" />}
          Resend
        </Button>
        <Button
          variant="secondary"
          mode="ghost"
          size="sm"
          onClick={() => onRevoke(member._id)}
          disabled={isRemoving}
          className="h-8 w-8 p-0"
        >
          {isRemoving ? (
            <RiLoader4Line className="size-4 animate-spin" />
          ) : (
            <RiDeleteBinLine className="size-4 text-destructive" />
          )}
        </Button>
      </div>
    </motion.div>
  );
}

/**
 * Team management for the community self-hosted build. The better-auth `TeamMembers` component is
 * unusable here because `/v1/better-auth/*` only exists in the enterprise build — this one talks to
 * the community organization/invite endpoints instead.
 */
export function TeamMembers() {
  const currentUserId = getCurrentUserId();
  const has = useHasPermission();
  const canManageMembers = has({ permission: PermissionsEnum.ORG_SETTINGS_WRITE });

  const [members, setMembers] = useState<SelfHostedMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [pendingMemberId, setPendingMemberId] = useState<string | null>(null);
  const [action, setAction] = useState<'remove' | 'role' | 'resend' | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRoleEnum>(MemberRoleEnum.VIEWER);

  const inviteEmailId = useId();

  const loadMembers = useCallback(async () => {
    try {
      const response = await get<{ data: SelfHostedMember[] }>('/organizations/members');
      setMembers(response.data || []);
    } catch (e: any) {
      showErrorToast(e.message || 'Failed to load organization members', 'Load Error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleCopyLink = async (token: string) => {
    const url = buildInviteUrl(token);

    if (await copyToClipboard(url)) {
      showSuccessToast('Invitation link copied to clipboard', 'Link Copied');
    } else {
      showErrorToast(url, 'Copy the invitation link manually');
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = inviteEmail.trim();
    if (!email) return;

    setIsInviting(true);
    try {
      await post('/invites', { body: { email, role: inviteRole } });

      const response = await get<{ data: SelfHostedMember[] }>('/organizations/members');
      setMembers(response.data || []);

      const invited = (response.data || []).find(
        (member) => member.invite?.email?.toLowerCase() === email.toLowerCase()
      );

      if (invited?.invite?.token && (await copyToClipboard(buildInviteUrl(invited.invite.token)))) {
        showSuccessToast(`${email} was invited — invitation link copied to clipboard`, 'Invitation Sent');
      } else {
        showSuccessToast(`${email} was invited`, 'Invitation Sent');
      }

      setInviteEmail('');
      setInviteRole(MemberRoleEnum.VIEWER);
    } catch (e: any) {
      showErrorToast(e.message || 'Failed to send invitation', 'Invitation Error');
    } finally {
      setIsInviting(false);
    }
  };

  const runMemberAction = async (
    memberId: string,
    kind: 'remove' | 'role' | 'resend',
    request: () => Promise<unknown>,
    successMessage: string,
    errorMessage: string
  ) => {
    setPendingMemberId(memberId);
    setAction(kind);
    try {
      await request();
      await loadMembers();
      showSuccessToast(successMessage, 'Team Updated');
    } catch (e: any) {
      showErrorToast(e.message || errorMessage, 'Team Update Error');
    } finally {
      setPendingMemberId(null);
      setAction(null);
    }
  };

  const handleRemove = (memberId: string) =>
    runMemberAction(
      memberId,
      'remove',
      () => del(`/organizations/members/${memberId}`),
      'Member removed from the organization',
      'Failed to remove member'
    );

  const handleChangeRole = (memberId: string, role: MemberRoleEnum) =>
    runMemberAction(
      memberId,
      'role',
      () => put(`/organizations/members/${memberId}/roles`, { body: { role } }),
      `Member role changed to ${getRoleLabel(role)}`,
      'Failed to update member role'
    );

  const handleResend = (memberId: string) =>
    runMemberAction(
      memberId,
      'resend',
      () => post('/invites/resend', { body: { memberId } }),
      'Invitation resent',
      'Failed to resend invitation'
    );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RiLoader4Line className="size-6 animate-spin text-foreground-600" />
      </div>
    );
  }

  const activeMembers = members.filter((member) => member.memberStatus !== MemberStatusEnum.INVITED);
  const pendingInvites = members.filter((member) => member.memberStatus === MemberStatusEnum.INVITED);

  return (
    <div className="space-y-6">
      <div className="border-b border-neutral-100 pb-4">
        <h2 className="text-lg font-semibold text-foreground-950">
          Members <span className="text-foreground-600">({activeMembers.length})</span>
        </h2>
        <p className="mt-1 text-sm text-foreground-600">Manage who has access to this organization</p>
      </div>

      {canManageMembers && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <div className="mb-4 flex items-center gap-2">
            <RiUserAddLine className="size-5 text-foreground-600" />
            <h3 className="text-sm font-medium text-foreground-950">Invite new member</h3>
          </div>

          <form onSubmit={handleInvite} className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor={inviteEmailId} className="sr-only">
                  Email address
                </label>
                <Input
                  id={inviteEmailId}
                  type="email"
                  value={inviteEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
                  placeholder="member@example.com"
                  required
                  disabled={isInviting}
                  className="h-10"
                />
              </div>
              <div className="w-32">
                <Select
                  value={inviteRole}
                  onValueChange={(value) => setInviteRole(value as MemberRoleEnum)}
                  disabled={isInviting}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={isInviting || !inviteEmail.trim()}
                variant="primary"
                mode="gradient"
                className="h-10 px-4"
              >
                {isInviting ? (
                  <RiLoader4Line className="size-4 animate-spin" />
                ) : (
                  <>
                    <RiAddCircleLine className="size-4" />
                    <span className="ml-1.5">Invite</span>
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-foreground-600">
              {ROLE_OPTIONS.find((option) => option.value === inviteRole)?.hint}. The invitation link is copied to your
              clipboard — share it if your self-hosted instance does not send emails.
            </p>
          </form>
        </div>
      )}

      {pendingInvites.length > 0 && canManageMembers && (
        <div className="rounded-lg border border-neutral-200 bg-white">
          <div className="border-b border-neutral-100 p-4">
            <h3 className="text-sm font-medium text-foreground-950">
              Pending Invitations <span className="text-foreground-600">({pendingInvites.length})</span>
            </h3>
          </div>
          <div className="px-4">
            <AnimatePresence>
              {pendingInvites.map((member) => (
                <InvitationListItem
                  key={member._id}
                  member={member}
                  onCopyLink={handleCopyLink}
                  onResend={handleResend}
                  onRevoke={handleRemove}
                  isResending={pendingMemberId === member._id && action === 'resend'}
                  isRemoving={pendingMemberId === member._id && action === 'remove'}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white">
        <div className="p-4">
          <AnimatePresence>
            {activeMembers.map((member) => (
              <MemberListItem
                key={member._id}
                member={member}
                currentUserId={currentUserId}
                onRemove={handleRemove}
                onChangeRole={handleChangeRole}
                isRemoving={pendingMemberId === member._id && action === 'remove'}
                isUpdatingRole={pendingMemberId === member._id && action === 'role'}
                canManageMembers={canManageMembers}
              />
            ))}
          </AnimatePresence>

          {activeMembers.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-foreground-600">No members found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
